import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core'
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading } from '@angular/router'

import { registerLocaleData } from '@angular/common'
import { HttpRequest, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http'
import localeDe from '@angular/common/locales/de'
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, provideMomentDateAdapter } from '@angular/material-moment-adapter'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { MatIconRegistry } from '@angular/material/icon'
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular'
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin'
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin'
import { provideStore, withNgxsDevelopmentOptions } from '@ngxs/store'
import {
  artificialDelayInterceptor,
  errorInterceptorFn,
  HTTP_ERROR_INTERCEPTOR_IGNORE_LIST,
  isCustomerFacingStage,
  provideKeinBrowserAutofill,
  provideSharedService,
  provideStageTitle,
  provideZahlenfelderOhneMausrad,
} from '@saniup-webapps/shared'
import 'moment/locale/de'
import { env } from '../environments/environment'
import { AppPreloadingStrategy } from './app.preloader'
import { routes } from './app.routes'
import { FinanzenPageState } from './store/finanzen-page.state'
import { ProduktDashboardState } from './store/produkt-dashboard.state'

registerLocaleData(localeDe)

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: AppPreloadingStrategy, useClass: AppPreloadingStrategy },
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass('material-symbols-outlined')
    }),
    provideZonelessChangeDetection(),
    // Reicht unbehandelte Promise-Fehler (z. B. ein fehlender Lazy-Chunk nach einem Deploy) an den
    // GlobalErrorHandlerService weiter, der dann neu laedt. Zoneless tut das sonst niemand.
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withPreloading(AppPreloadingStrategy),
      withComponentInputBinding(),
    ),
    provideAuth0(env().auth),
    provideHttpClient(withXhr(), withInterceptors([errorInterceptorFn(), authHttpInterceptorFn, artificialDelayInterceptor])),
    provideMomentDateAdapter({
      parse: {
        dateInput: ['l', 'LL'],
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
      },
    }),
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    // Die Adressen hier sind die von Patient:innen, nicht die der Mitarbeiterin (Begruendung am Provider).
    provideKeinBrowserAutofill(),
    provideZahlenfelderOhneMausrad(),
    provideStageTitle(),
    provideSharedService({
      loginConfig: {
        internalRedirectUri: '/internal',
      },
      stage: env().stage,
      anrede: 'du',
    }),
    {
      provide: HTTP_ERROR_INTERCEPTOR_IGNORE_LIST,
      useValue: [
        (req: HttpRequest<unknown>) => {
          return (
            req.method === 'GET' &&
            (req.url.includes('/api/leistungserbringer/responseplus/') || req.url.includes('/api/leistungserbringer/logo/'))
          )
        },
      ],
    },
    provideStore(
      [ProduktDashboardState, FinanzenPageState],
      { developmentMode: !isCustomerFacingStage(env().stage) },
      withNgxsDevelopmentOptions({ warnOnUnhandledActions: true }),
      withNgxsReduxDevtoolsPlugin({ maxAge: 25, disabled: isCustomerFacingStage(env().stage) }),
      withNgxsStoragePlugin({ keys: [ProduktDashboardState, FinanzenPageState] }),
    ),
  ],
}
